/**
 * Bending Spoons SDK (Orion experiments + PicoX analytics) wiring for
 * listings-webapp. Mirrors the Valkyrie-app pattern documented in
 * `docs/orion-feature-flags.md` (creator-navigation PR #381):
 *
 *   - The provider is mounted once at the highest stable spot in the React
 *     tree (`pages/_app.page.tsx`).
 *   - This module owns the typed `Settings` shape for the whole app.
 *     One key per Orion flag, default = current production behavior.
 *   - Feature code never imports `useOrionSettings` directly; it imports a
 *     typed wrapper hook from this file (e.g. `useIsRelatedEventsDenseAdsEnabled`).
 *
 * To add a new flag: append one key + one default to `ListingsWebappSettings`
 * / `DEFAULT_SETTINGS`, then export a one-line wrapper hook below.
 */
import { useMemo } from 'react';
import { createAppDefaults } from '@eventbrite/bsp-sdk';
import { useOrionSettings } from '@eventbrite/bsp-sdk/react';

export type ListingsWebappSettings = {
  enable_increase_impressions_on_related_events: boolean;
  show_follow_venue_cta: boolean;
  show_follow_artist_cta: boolean;
  members_experiments_excluded_organizer_ids: string[];
};

const DEFAULT_SETTINGS = createAppDefaults<ListingsWebappSettings>({
  enable_increase_impressions_on_related_events: false,
  show_follow_venue_cta: false,
  show_follow_artist_cta: false,
  members_experiments_excluded_organizer_ids: [],
});

export const useBspConfig = () =>
  useMemo(() => {
    const domain = typeof window !== 'undefined' ? window.location.hostname : '';
    const environment = domain.includes('evbqa')
      ? 'staging'
      : domain.includes('localhost')
        ? 'development'
        : 'production';

    return {
      appName: 'listings-webapp',
      environment,
      defaultOrionSettings: DEFAULT_SETTINGS,
      defaultSegmentations: {},
    } as const;
  }, []);

/**
 * Gate for the denser related-events ad layout
 * (`enable_increase_impressions_on_related_events` Orion flag).
 *
 *   false (default / control) — request `slots: 2` and interleave ads via
 *     `interleaveArrays(promoted, organic)` (positions [1, 3]).
 *   true  (treatment)         — request `slots: 3` and interleave at
 *     positions [1, 2, 4] (ads → ads → organic → ads → organic).
 *
 * Same key + same default in eb-ui's `packages/attendee/listings/src/components/_shared`
 * and `packages/attendee/event-recommendations/src` so all three surfaces
 * ramp in lockstep.
 *
 * Excluded organizers: when `parentEventOrganizerId` is one of the ids in
 * the Orion setting `members_experiments_excluded_organizer_ids`, this
 * hook returns `false` regardless of the assigned segment — mirroring
 * the eb-ui checkout / event-recommendations gate so all three surfaces
 * opt the same events out in lockstep.
 */
export const useIsRelatedEventsDenseAdsEnabled = (parentEventOrganizerId?: string): boolean => {
  const { settings } = useOrionSettings<ListingsWebappSettings>();
  const isOrganizerExcluded =
    !!parentEventOrganizerId && settings.members_experiments_excluded_organizer_ids.includes(parentEventOrganizerId);
  if (isOrganizerExcluded) {
    return false;
  }
  return settings.enable_increase_impressions_on_related_events;
};

/** Gate for the Follow Venue CTA (`show_follow_venue_cta` Orion flag). */
export const useShowFollowVenueCta = (): boolean => {
  const { settings } = useOrionSettings<ListingsWebappSettings>();
  return settings.show_follow_venue_cta;
};

/** Gate for the Follow Artist CTA (`show_follow_artist_cta` Orion flag). */
export const useShowFollowArtistCta = (): boolean => {
  const { settings } = useOrionSettings<ListingsWebappSettings>();
  return settings.show_follow_artist_cta;
};
